import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import {
  TransactionState,
  TransactionResponse,
  GetTransactionsParams,
  ApiError,
} from "./types/transaction-types";

const initialState: TransactionState = {
  transactions: [],
  pagination: null,
  user: null,
  filters: {
    type: null,
    status: null,
    payment_method: null,
    start_date: null,
    end_date: null,
    search: null,
  },
  loading: false,
  error: null,
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Get Transactions by User ID
export const getMyTransactions = createAsyncThunk<
  TransactionResponse,
  Omit<GetTransactionsParams, "user_id">,
  { rejectValue: ApiError; state: RootState }
>("transactions/getTransactionsById", async (params, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  const currentUser = thunkAPI.getState().auth.user;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${API_URL}/my/transactions?${queryParams.toString()}`;

    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch transactions" }
    );
  }
});

// ✅ Transaction Slice
const transactionSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    clearTransactions: (state) => {
      state.transactions = [];
      state.pagination = null;
      state.user = null;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    resetTransactionState: (state) => {
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data.data;
        state.pagination = {
          current_page: action.payload.data.current_page,
          last_page: action.payload.data.last_page,
          per_page: action.payload.data.per_page,
          total: action.payload.data.total,
          from: action.payload.data.from,
          to: action.payload.data.to,
        };
        state.user = action.payload.user;
        state.filters = action.payload.filters_applied;
      })
      .addCase(getMyTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch transactions";
      });
  },
});

export const {
  clearTransactions,
  updateFilters,
  clearFilters,
  resetTransactionState,
} = transactionSlice.actions;
export default transactionSlice.reducer;
